// Sync status indicator component
// Shows offline status and pending upload/survey count

import { useState, useEffect } from 'react'
import useSurveyStore from '../stores/surveyStore'

export default function SyncStatus({ isOnline, pendingUploads, pendingSurveys = 0, syncing, syncingSurveys = false }) {
  const syncPendingPhotos = useSurveyStore(state => state.syncPendingPhotos)
  const syncPendingSurveys = useSurveyStore(state => state.syncPendingSurveys)
  const refreshPendingCount = useSurveyStore(state => state.refreshPendingCount)
  const [syncDuration, setSyncDuration] = useState(0)
  const [lastError, setLastError] = useState(null)

  // Track how long sync has been running
  const isSyncingAny = syncing || syncingSurveys
  useEffect(() => {
    let interval
    if (isSyncingAny) {
      setSyncDuration(0)
      interval = setInterval(() => {
        setSyncDuration(d => d + 1)
      }, 1000)
    } else {
      setSyncDuration(0)
    }
    return () => clearInterval(interval)
  }, [isSyncingAny])

  const handleSync = async () => {
    setLastError(null)
    // Sync both surveys and photos
    if (pendingSurveys > 0) {
      const surveyResult = await syncPendingSurveys()
      if (surveyResult && !surveyResult.success && surveyResult.error) {
        setLastError(surveyResult.error)
        return
      }
    }
    if (pendingUploads > 0) {
      const photoResult = await syncPendingPhotos()
      if (photoResult && !photoResult.success && photoResult.error) {
        setLastError(photoResult.error)
      }
    }
  }

  const handleForceSync = async () => {
    setLastError(null)
    // Force sync both
    await syncPendingSurveys()
    const result = await syncPendingPhotos(true) // Force reset
    if (result && !result.success && result.error) {
      setLastError(result.error)
    }
  }

  // Refresh count on mount and periodically
  useEffect(() => {
    refreshPendingCount()
    const interval = setInterval(refreshPendingCount, 30000) // Check every 30s
    return () => clearInterval(interval)
  }, [refreshPendingCount])

  // Don't show anything if online and nothing pending
  const totalPending = pendingUploads + pendingSurveys
  const isSyncing = syncing || syncingSurveys

  if (isOnline && totalPending === 0 && !isSyncing && !lastError) {
    return null
  }

  const syncStuck = isSyncing && syncDuration > 30 // Show force option after 30s

  return (
    <div className="sync-status-bar">
      {!isOnline && (
        <div className="sync-status offline">
          <span className="status-icon">&#x26A0;</span>
          <span>Offline - data saved locally</span>
        </div>
      )}

      {isOnline && isSyncing && (
        <div className="sync-status syncing">
          <span className="status-icon spinning">&#x21BB;</span>
          <span>
            {syncingSurveys ? 'Syncing surveys...' : 'Syncing photos...'}
            {syncDuration > 5 ? ` (${syncDuration}s)` : ''}
          </span>
          {syncStuck && (
            <button className="sync-button force" onClick={handleForceSync}>
              Force Reset
            </button>
          )}
        </div>
      )}

      {isOnline && !isSyncing && totalPending > 0 && (
        <div className="sync-status pending">
          <span className="status-icon">&#x2601;</span>
          <span>
            {pendingSurveys > 0 && `${pendingSurveys} survey${pendingSurveys !== 1 ? 's' : ''}`}
            {pendingSurveys > 0 && pendingUploads > 0 && ', '}
            {pendingUploads > 0 && `${pendingUploads} photo${pendingUploads !== 1 ? 's' : ''}`}
            {' '}pending
          </span>
          <button className="sync-button" onClick={handleSync}>
            Sync Now
          </button>
        </div>
      )}

      {lastError && (
        <div className="sync-status error">
          <span className="status-icon">&#x26A0;</span>
          <span>Sync error: {lastError}</span>
          <button className="sync-button" onClick={() => setLastError(null)}>
            Dismiss
          </button>
        </div>
      )}

      <style>{`
        .sync-status-bar {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 1000;
        }

        .sync-status {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 16px;
          font-size: 14px;
          font-weight: 500;
        }

        .sync-status.offline {
          background: #FEF3C7;
          color: #92400E;
          border-top: 1px solid #F59E0B;
        }

        .sync-status.syncing {
          background: #DBEAFE;
          color: #1E40AF;
          border-top: 1px solid #3B82F6;
        }

        .sync-status.pending {
          background: #FEE2E2;
          color: #991B1B;
          border-top: 1px solid #EF4444;
        }

        .status-icon {
          font-size: 18px;
        }

        .status-icon.spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .sync-button {
          margin-left: 12px;
          padding: 4px 12px;
          background: #991B1B;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
        }

        .sync-button:hover {
          background: #7F1D1D;
        }

        .sync-button.force {
          background: #DC2626;
        }

        .sync-button.force:hover {
          background: #B91C1C;
        }

        .sync-status.error {
          background: #FEE2E2;
          color: #991B1B;
          border-top: 1px solid #EF4444;
        }
      `}</style>
    </div>
  )
}
